// Getting a profile photo ready to send.
//
// The person picks a photo, then moves and zooms it inside the circle (see PhotoCropper).
// This side draws just that part at no more than 512 pixels square, so a 12 megapixel phone
// photo goes up as about 100 KB. The server then makes the stored copy (a 256 pixel JPEG)
// and checks the limits again for itself.

// Keep in step with MIN_SOURCE_SIDE in src/helpers/profileImages.ts.
export const MIN_PHOTO_SIDE = 128;
// What is sent: twice the stored size, so the server has room to shrink it cleanly.
export const UPLOAD_SIDE = 512;
// Only a guard against picking a video by mistake. The photo is shrunk here before it
// is sent, so the server's own 5 MB limit is never reached from this page.
export const MAX_PICKED_BYTES = 40 * 1024 * 1024;
// Zooming further than this would crop to a patch smaller than MIN_PHOTO_SIDE, or get
// silly on a huge photo.
export const MAX_ZOOM = 5;

export const ACCEPTED_PHOTO_TYPES = 'image/jpeg,image/png';

export type Area = { x: number; y: number; width: number; height: number };

export interface LoadedPhoto {
  /** An object URL for the cropper to show. */
  url: string;
  image: HTMLImageElement;
  width: number;
  height: number;
  release: () => void;
}

// How far in the person may zoom: until the square they have chosen is MIN_PHOTO_SIDE
// across in the original photo, and never past MAX_ZOOM.
export const maxZoomFor = (width: number, height: number) =>
  Math.max(1, Math.min(MAX_ZOOM, Math.min(width, height) / MIN_PHOTO_SIDE));

// The size to draw the chosen square at: its own size, up to UPLOAD_SIDE.
export const outputSideFor = (area: Area) => Math.max(1, Math.min(Math.round(area.width), UPLOAD_SIDE));

// Checks the file and decodes it. Throws an Error whose message can be shown as it is.
export const loadPhoto = async (file: File): Promise<LoadedPhoto> => {
  if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
    throw new Error('Use a JPG or PNG photo.');
  }
  if (file.size > MAX_PICKED_BYTES) {
    throw new Error('That file is too big to be a photo.');
  }

  const url = URL.createObjectURL(file);
  let image: HTMLImageElement;
  try {
    // An <img> turns phone photos upright from their EXIF, and so does drawing it later.
    image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('unreadable'));
      el.src = url;
    });
  } catch (error) {
    URL.revokeObjectURL(url);
    throw new Error('That photo could not be read. Use a JPG or PNG.');
  }

  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (Math.min(width, height) < MIN_PHOTO_SIDE) {
    URL.revokeObjectURL(url);
    throw new Error(`That photo is too small. It needs to be at least ${MIN_PHOTO_SIDE} by ${MIN_PHOTO_SIDE} pixels.`);
  }
  return { url, image, width, height, release: () => URL.revokeObjectURL(url) };
};

// Draws the chosen square and returns it as a data: URL of a JPEG, ready for
// POST /user/uploadProfileImage.
export const renderCroppedPhoto = (photo: LoadedPhoto, area: Area): string => {
  const side = outputSideFor(area);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  // JPEG has no transparency; the server fills with white too.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, side, side);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo.image, area.x, area.y, area.width, area.height, 0, 0, side, side);
  return canvas.toDataURL('image/jpeg', 0.9);
};

// Shown in the circle when there is no photo.
export const initialFor = (username?: string) => ((username || '').trim().charAt(0) || '?').toUpperCase();
